/**
 * events.ts — Per-year modifiers derived from the SimulationEvent list.
 *
 * Called once per simulation year. Returns delta values that montecarlo.ts
 * applies on top of the base income/expense/asset calculations.
 *
 * Design: pure functions, no side effects. All randomness (market shock
 * severity) is deterministic given the fixed params the user configured;
 * stochastic draws still happen in montecarlo.ts via Box-Muller.
 */

import type { SimulationEvent, SimulationEventParams } from './types'
import {
  DEFAULT_JOB_LOSS_INCOME_FRACTION,
  DEFAULT_MEDICAL_COST,
  DEFAULT_MEDICAL_DEDUCTIBLE,
  DEFAULT_MEDICAL_SPREAD_YEARS,
  DEFAULT_CHILDCARE_END_YEAR_OFFSET,
  DEFAULT_COLLEGE_END_YEAR_OFFSET,
  DEFAULT_MARKET_LOSS_PCT,
  DEFAULT_MARKET_RECOVERY_YEARS,
  DEFAULT_MARKET_RETURN_PENALTY,
  DEFAULT_HOME_PRICE,
  DEFAULT_DOWN_PAYMENT_PCT,
  DEFAULT_MORTGAGE_RATE,
  DEFAULT_MORTGAGE_TERM_YEARS,
  DEFAULT_DIVORCE_ASSET_FRACTION,
  MONTHS_PER_YEAR,
  EVENT_ID_BASE,
  EVENT_ID_SLICE_START,
  EVENT_ID_SLICE_END,
} from './defaults'

export interface YearEventModifiers {
  /** Multiplier on base W2 income (1.0 = unchanged, 0.0 = no income) */
  incomeMultiplier: number
  /** Override W2 income entirely when > 0 */
  incomeOverride: number | null
  /** Override equity comp when > 0 */
  equityOverride: number | null
  /** One-time liquid asset additions (windfall, bonus) */
  liquidDelta: number
  /** Additional expense this year (medical, major expense, tuition) */
  extraExpense: number
  /** Fraction to multiply portfolio value by (market shock: 0.6) */
  portfolioShockMultiplier: number
  /** Subtract from blended return mean during recovery */
  returnPenalty: number
  /** Additional annual housing expense (home purchase mortgage) */
  extraHousing: number
  /** Description tags for the scenario indicator */
  activeLabels: string[]
}

export function neutralModifiers(): YearEventModifiers {
  return {
    incomeMultiplier: 1.0,
    incomeOverride: null,
    equityOverride: null,
    liquidDelta: 0,
    extraExpense: 0,
    portfolioShockMultiplier: 1.0,
    returnPenalty: 0,
    extraHousing: 0,
    activeLabels: [],
  }
}

export function computeEventModifiers(
  events: SimulationEvent[],
  yearOffset: number,
  baseW2Income: number,
  inflationFactor: number,
): YearEventModifiers {
  const m = neutralModifiers()

  for (const ev of events) {
    const rel = yearOffset - ev.startYearOffset  // years since event started
    if (rel < 0) continue                        // event hasn't started yet

    const p: SimulationEventParams = ev.params
    const duration = p.durationYears ?? 0        // 0 = permanent

    // Is this event still active?
    const active = duration === 0 || rel < duration

    switch (ev.type) {
      case 'promotion': {
        if (!active) break
        // If recurs, check we're in a recurring window
        const everyN = p.recurEveryYears ?? 0
        if (everyN > 0 && rel > 0 && rel % everyN !== 0) {
          // Between recurrences: apply the raise compound from last recurrence
        }
        if (p.newAnnualIncome && p.newAnnualIncome > 0) m.incomeOverride = p.newAnnualIncome
        else if (p.raisePct && p.raisePct > 0) m.incomeMultiplier *= (1 + p.raisePct)
        if (p.newEquityComp && p.newEquityComp > 0) m.equityOverride = p.newEquityComp
        m.activeLabels.push(ev.label || 'Promotion')
        break
      }

      case 'job-loss': {
        if (!active) break
        const frac = p.incomeFraction ?? DEFAULT_JOB_LOSS_INCOME_FRACTION
        m.incomeMultiplier *= frac
        m.equityOverride = 0
        m.activeLabels.push(ev.label || 'Job Loss')
        break
      }

      case 'sabbatical': {
        if (!active) break
        const frac = p.incomeFraction ?? DEFAULT_JOB_LOSS_INCOME_FRACTION
        m.incomeMultiplier *= frac
        m.equityOverride = 0
        m.activeLabels.push(ev.label || 'Sabbatical')
        break
      }

      case 'disability': {
        if (!active) break
        const frac = p.incomeFraction ?? DEFAULT_JOB_LOSS_INCOME_FRACTION
        m.incomeMultiplier *= frac
        m.activeLabels.push(ev.label || 'Disability')
        break
      }

      case 'flat-bonus': {
        const everyN = p.recurEveryYears ?? 0
        const isThisYear = rel === 0 || (everyN > 0 && rel % everyN === 0)
        if (!isThisYear) break
        const totalBonus = p.amount ?? 0
        const spread = p.spreadYears ?? 1
        m.liquidDelta += (totalBonus / spread) * inflationFactor
        m.activeLabels.push(ev.label || 'Bonus')
        break
      }

      case 'windfall': {
        if (rel !== 0) break  // one-time at start year
        const total = p.amount ?? 0
        const spread = p.spreadYears ?? 1
        m.liquidDelta += (total / spread) * inflationFactor
        m.activeLabels.push(ev.label || 'Windfall')
        break
      }

      case 'medical-emergency': {
        if (rel !== 0) break  // one-time shock
        let outOfPocket = p.cost ?? DEFAULT_MEDICAL_COST
        if (p.insured) outOfPocket = Math.min(outOfPocket, p.deductible ?? DEFAULT_MEDICAL_DEDUCTIBLE)
        const spread = p.spreadYears ?? DEFAULT_MEDICAL_SPREAD_YEARS
        m.extraExpense += outOfPocket / spread
        m.activeLabels.push(ev.label || 'Medical Emergency')
        break
      }

      case 'major-expense': {
        const spread = p.spreadYears ?? 1
        if (rel >= spread) break
        m.extraExpense += ((p.amount ?? 0) / spread) * inflationFactor
        m.activeLabels.push(ev.label || 'Major Expense')
        break
      }

      case 'childcare':
      case 'college-tuition':
      case 'long-term-care': {
        const defaultEnd = ev.type === 'childcare'
          ? (ev.startYearOffset + DEFAULT_CHILDCARE_END_YEAR_OFFSET)
          : (ev.startYearOffset + DEFAULT_COLLEGE_END_YEAR_OFFSET)
        const endOffset = p.endYearOffset ?? defaultEnd
        if (yearOffset > endOffset) break
        m.extraExpense += (p.annualCost ?? 0) * inflationFactor
        m.activeLabels.push(ev.label || ev.type)
        break
      }

      case 'market-shock': {
        if (rel === 0) {
          m.portfolioShockMultiplier = 1 - (p.portfolioLossPct ?? DEFAULT_MARKET_LOSS_PCT)
          m.activeLabels.push(ev.label || 'Market Shock')
        }
        const recYears = p.recoveryYears ?? DEFAULT_MARKET_RECOVERY_YEARS
        if (rel < recYears) {
          m.returnPenalty += p.recoveryReturnPenalty ?? DEFAULT_MARKET_RETURN_PENALTY
          if (rel > 0) m.activeLabels.push(ev.label || 'Market Recovery')
        }
        break
      }

      case 'home-purchase': {
        if (!active) break
        if (rel === 0) {
          // Down payment as one-time liquid drain
          const dp = (p.homePrice ?? DEFAULT_HOME_PRICE) * (p.downPaymentPct ?? DEFAULT_DOWN_PAYMENT_PCT)
          m.liquidDelta -= dp
        }
        // Mortgage P+I replaces rent; modeled as extra housing delta
        if (p.homePrice && p.mortgageRate) {
          const principal = p.homePrice * (1 - (p.downPaymentPct ?? DEFAULT_DOWN_PAYMENT_PCT))
          const r = (p.mortgageRate ?? DEFAULT_MORTGAGE_RATE) / MONTHS_PER_YEAR
          const n = (p.mortgageTermYears ?? DEFAULT_MORTGAGE_TERM_YEARS) * MONTHS_PER_YEAR
          const monthly = (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1)
          m.extraHousing += monthly * MONTHS_PER_YEAR
        }
        m.activeLabels.push(ev.label || 'Home Purchase')
        break
      }

      case 'divorce': {
        if (rel !== 0) break
        // Modeled as a one-time liquid shock (applied in montecarlo)
        const retain = p.assetRetentionFraction ?? DEFAULT_DIVORCE_ASSET_FRACTION
        m.liquidDelta -= baseW2Income * (1 - retain)  // crude proxy; real split done in montecarlo
        m.activeLabels.push(ev.label || 'Divorce')
        break
      }
    }
  }

  return m
}

/** Generate a unique id for a new event */
export function newEventId(): string {
  return `evt-${Date.now()}-${Math.random().toString(EVENT_ID_BASE).slice(EVENT_ID_SLICE_START, EVENT_ID_SLICE_END)}`
}

/** Human-readable default label for an event type */
export function defaultEventLabel(type: SimulationEvent['type']): string {
  const labels: Record<SimulationEvent['type'], string> = {
    'promotion':       'Promotion',
    'job-loss':        'Job Loss',
    'flat-bonus':      'Bonus',
    'medical-emergency': 'Medical Emergency',
    'disability':      'Disability',
    'market-shock':    'Market Shock',
    'major-expense':   'Major Expense',
    'windfall':        'Windfall / Inheritance',
    'childcare':       'Childcare',
    'college-tuition': 'College Tuition',
    'home-purchase':   'Home Purchase',
    'divorce':         'Divorce',
    'long-term-care':  'Long-Term Care',
    'sabbatical':      'Sabbatical',
  }
  return labels[type] ?? type
}
